// --------------------------
// IMPORTS
// --------------------------
import {
  Row,
  Col,
  Form,
  DatePicker,
  Input,
  message,
  Button,
  Table,
  Typography,
  Modal,
  Select,
  Skeleton,
  Upload,
  Checkbox,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import APICaller from "../../utils/APICaller";
import moment from "moment";

import Column from "antd/es/table/Column";

import dayjs from "dayjs";
import { SlideshowLightbox } from "lightbox.js-react";
import useCurrentPath, {
  decryptData,
  draggerProps,
  getCurrencyCode,
  getCurrencyCodeFromLocation,
  getCurrencyOptionByCode,
  getDepartmentLabel,
  getExpenseCodeLabel,
  getProjectCodeLabel,
  getRateByDate,
  handleDelete,
} from "../../utils/Constants";
import CustomLoader from "../../utils/CustomLoader";
import {
  discardExpenseThunk,
  getListOfExpenseDetailThunk,
  getUserListThunk,
  publishExpenseThunk,
} from "../../store/thunks/listOfExpensesThunk";
import {
  getAdminApproverListThunk,
  getBlockedDateThunk,
} from "../../store/thunks/addNewExpenseThunk";
import media from "../../media";
import SubmitButton from "../../components/atoms/Buttons/SubmitButton";
import AddNewButton from "../../components/atoms/Buttons/AddNewButton";
import Dragger from "antd/es/upload/Dragger";
import {
  getCurrencyListDetailThunk,
  getCurrencyListThunk,
  getDepartmentListThunk,
  getDiemListThunk,
  getExpenseCodeListThunk,
  getLocationListThunk,
  getProjectCodeListThunk,
  getUserListWithDetailThunk,
  getVendorListThunk,
} from "../../store/thunks/masterDataThunk";

// --------------------------
// MAIN COMPONENT
// --------------------------
export default function DraftExpenseRequest({
  isVisible,
  setIsVisible,
  recordKey,
  onCloseReset,
}) {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const currentPath = useCurrentPath();
  const location = useLocation();
  const isPublishIconClick = location?.state?.publishDraftExpense;
  const navigate = useNavigate();

  const id = recordKey;
  const [form] = Form.useForm();
  const { RangePicker } = DatePicker;
  const hasInitialized = useRef(false);
  const tableTotalRef = useRef(0);
  const hasExecuted = useRef(false); // track first run
  const backendTravelRef = useRef(null);

  // --------------------------
  // REDUX STATE
  // --------------------------
  let storeRole = useSelector((state) => state.auth.role);
  let storeToken = useSelector((state) => state.auth.accessToken);
  let storeLIUId = useSelector((state) => state.auth.lIUId);

  let storeUserInfo = useSelector((state) => state.auth.userInfo);
  storeUserInfo = decryptData(storeUserInfo);
  const storeEmpID = storeUserInfo?.user?.empID;
  const storeEmpName = storeUserInfo?.user?.name;
  const storeEmpDepartmentID = storeUserInfo?.user?.departmentID;
  const storeEmpDepartmentName = storeUserInfo?.userDepartment;
  const storeEmpDepartmentCode = storeUserInfo?.userDepartmentCode;

  const storeAccessTokenExpiresOn = useSelector(
    (state) => state.auth.accessTokenExpiresOn,
  );

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [showLoader, setShowLoader] = useState(true);
  const [savedExpenseDetail, setSavedExpenseDetail] = useState();
  const [formAction, setFormAction] = useState("");
  const [newFileCount, setNewFileCount] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [addExpenseValues, setAddExpenseValues] = useState(null);
  const [adminApprovers, setAdminApprovers] = useState([]);
  const [projectCodes, setProjectCodes] = useState([]);
  const [blockedDate, setBlockedDate] = useState(null);
  const [expenseCodes, setExpenseCodes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [allCurrencies, setAllCurrencies] = useState([]);
  const [currenciesInfo, setCurrenciesInfo] = useState([]);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [mediaItem, setMediaItem] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [currencyRates, setCurrencyRates] = useState({});
  const [totalReimbursement, setTotalReimbursement] = useState(0);
  const [usersDetailList, setUsersDetailList] = useState([]);
  const [currenciesDetailList, setCurrenciesDetailList] = useState([]);
  const [natureOfExpenseSelectedValue, setNatureOfExpenseSelectedValue] =
    useState();
  const [locations, setLocations] = useState([]);
  const [diems, setDiems] = useState([]);
  const [errors, setErrors] = useState([]);
  const [changedExpenseValues, setChangedExpenseValues] = useState({
    empName: "",
    empID: "",
    departmentID: "",
    expensePeriodFromDate: "",
    expensePeriodToDate: "",
    expenseEmpIDRole: "",
    businessPurpose: "",
    natureOfExpense: "",
    expensesDetails: "",
    subTotal: "",
    totalReimbursement: "",
    location: "",
    numberOfDays: 0,
    perDiem: 0,
    exchangeRate: 0,
    totalForeignAmount: 0,
    totalINRAmount: 0,
    travelSettlementFromDate: "",
    travelSettlementToDate: "",
    advanceAmount: 0,
    travelSettleMediaInfo: [],
  });
  const [showAdvanceAmount, setShowAdvanceAmount] = useState(false);

  const [rows, setRows] = useState([
    {
      date: "",
      description: "",
      departmentID: "",
      expenseCodeID: "",
      currencyID: "",
      recharge: "No",
      cost: "",
      mediaInfo: [],
    },
  ]);
  const [loading, setLoading] = useState(true);
  const [natureOfExpense, setNatureOfExpense] = useState([
    { label: "Already Paid - Filling", value: "Already Paid - Filling" },
    { label: "Reimbursement", value: "Reimbursement" },
    { label: "Travel Settlement", value: "Travel Settlement" },
    { label: "Vendor Bill", value: "Vendor Bill" },
  ]);

  const [rechargeOpts, setRechargeOpts] = useState([
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
  ]);

  const [travelMediaItem, setTravelMediaItem] = useState(null);

  // --------------------------
  // UTILITY FUNCTIONS
  // --------------------------
  const normalizeCurrencyValue = (currencyOption) =>
    currencyOption?.value ?? null;
  const effectiveLocation =
    changedExpenseValues?.location ||
    changedExpenseValues?.travelSettlement?.[0]?.location;
  const locationObj = useMemo(() => {
    if (!effectiveLocation || !Array.isArray(locations)) return null;

    return locations.find(
      (item) => String(item.value) === String(effectiveLocation),
    );
  }, [effectiveLocation, locations]);

  //Detect currency from location
  const locationCurrency = useMemo(() => {
    if (!locationObj) return null;
    return getCurrencyCodeFromLocation(locationObj);
  }, [locationObj]);

  const getUserDiem = (user, diems) => {
    if (!user?.diemId || !Array.isArray(diems)) return null;

    return diems.find((d) => d._id === user.diemId) || null;
  };

  const buildPerDiemRates = (user, diems) => {
    const diem = getUserDiem(user, diems);
    if (!diem) return {};

    return {
      INR: Number(diem.inr) || 0,
      AUD: Number(diem.aud) || 0,
      USD: Number(diem.usd) || 0,
      GBP: Number(diem.gbp) || 0,
      CAD: Number(diem.cad) || 0,
      EU: Number(diem.eu) || 0,
    };
  };

  const getExchangeRateToINR = (
    currencyCode,
    currenciesInfo,
    effectiveDate,
  ) => {
    if (!currencyCode) return 1;

    const currency = currenciesInfo.find(
      (c) => String(c.code) === String(currencyCode),
    );

    if (!currency?.currencyRateHistory?.length) return 1;

    const sorted = [...currency.currencyRateHistory].sort(
      (a, b) => new Date(b.effectiveDateTime) - new Date(a.effectiveDateTime),
    );

    const applicable =
      sorted.find(
        (r) => new Date(r.effectiveDateTime) <= new Date(effectiveDate),
      ) || sorted[0];

    return Number(applicable?.rate) || 1;
  };

  const PER_DIEM_RATES = useMemo(() => {
    return buildPerDiemRates(storeUserInfo?.user, diems);
  }, [storeUserInfo?.user?.diemId, diems]);

  const calculateTotal = (rows, travelINR, isTravel) => {
    const tableTotal = rows.reduce((sum, r) => {
      return sum + Number(r.costInINR || 0);
    }, 0);

    tableTotalRef.current = tableTotal;

    return isTravel
      ? (tableTotal + Number(travelINR || 0)).toFixed(2)
      : tableTotal.toFixed(2);
  };

  const parseToFloat = (value) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const images =
    (changedExpenseValues?.expensesDetails &&
      changedExpenseValues?.expensesDetails?.flatMap((detail) =>
        detail.mediaInfo
          ?.filter(
            (file) =>
              file?.savedFileType === "jpeg" ||
              file?.savedFileType === "jpg" ||
              file?.savedFileType === "png",
          )
          ?.map(
            (file) => process.env.REACT_APP_BACKEND + "/" + file?.savedFilepath,
          ),
      )) ||
    [];

  const blockedOn = blockedDate?.blockedOn && dayjs(blockedDate?.blockedOn);
  const blockedOnMonthStart = blockedOn && blockedOn.startOf("month");

  // sticky code start here
  const wrapperRef = useRef(null);
  const stickyRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);
  const STICKY_OFFSET = 54;

  const handleScroll = () => {
    if (!wrapperRef.current || !stickyRef.current) return;

    const wrapperTop = wrapperRef.current.getBoundingClientRect().top;
    const stickyTop = stickyRef.current.getBoundingClientRect().top;

    // Trigger when sticky reaches 55px from top
    setIsSticky(stickyTop <= wrapperTop + STICKY_OFFSET);
  };

  // --------------------------
  // HANDLERS
  // --------------------------
  const resetAdvanceAndMedia = () => {
    // Reset form fields
    form.setFieldsValue({
      travelSettlement: [
        {
          advanceAmount: 0,
        },
      ],
      travelSettleMediaInfo: [],
    });

    // Reset main state
    setChangedExpenseValues((prev) => {
      const updatedSettlement = [...(prev.travelSettlement || [])];

      if (updatedSettlement.length > 0) {
        updatedSettlement[0] = {
          ...updatedSettlement[0],
          advanceAmount: 0,
          travelSettleMediaInfo: [],
        };
      }

      return {
        ...prev,
        advanceAmount: 0,
        travelSettlement: updatedSettlement,
      };
    });

    // Hide section
    setShowAdvanceAmount(false);
  };
  const updateRowFiles = (index, fileSources, updatedRows) => {
    if (!updatedRows[index].mediaInfo) {
      updatedRows[index].mediaInfo = [];
    }
    updatedRows[index].mediaInfo = fileSources;

    setRows(updatedRows);

    // Update the mediaInfo in the specific detail
    const updatedDetails = (updatedRows || [])?.map((item, idx) => {
      if (idx === index) {
        return {
          ...item,
          ["mediaInfo"]: updatedRows[index]["mediaInfo"],
        };
      }
      return item;
    });

    // // Update the state or parent component's state with the updated savedExpenseDetail
    setChangedExpenseValues((prevState) => ({
      ...prevState,
      expensesDetails: updatedDetails,
    }));
  };

  function getMinDate(dateStr) {
    const date = new Date(dateStr); // e.g., "2025-05-22"
    const year = date.getFullYear();
    const month = date.getMonth(); // Current month (0-indexed)

    // Set to 0th day of current month to get last day of previous month
    return new Date(year, month, 0); // e.g., 30 April 2025
  }

  // Preprocess rows to add a stable 'id' if missing
  const rowsWithIds = useMemo(() => {
    return rows?.map((item, index) => ({
      ...item,
      id: item.id ?? `row-${index}-${Math.random().toString(36).substr(2, 9)}`,
    }));
  }, [rows]);

  const validationRules = {
    date: [{ required: true, message: "Please select a date" }],
    description: [{ required: true, message: "Please enter a description" }],
    departmentID: [{ required: true, message: "Please select a department" }],
    expenseCodeID: [
      { required: true, message: "Please select an expense code" },
    ],
    currencyID: [{ required: true, message: "Please select a currency" }],
    cost: [
      { required: true, message: "Please enter a cost" },
      {
        pattern: /^[0-9]*\.?[0-9]+$/,
        message: "Only numbers and decimals are allowed",
      },
    ],
    recharge: [{ required: true, message: "Please select recharge" }],
    upload: [{ required: true, message: "Please choose a file" }],
  };

  // Add Expense API
  const onFinish = (values) => {
    const formattedDates = Array.isArray(values?.expensePeriodFromDate)
      ? values.expensePeriodFromDate?.map((date) =>
          dayjs(date).format("YYYY-MM-DD"),
        )
      : [];

    const dateToCheck = formattedDates[0] && dayjs(formattedDates[0]);
    const isBackDated = dateToCheck.isBefore(blockedOnMonthStart);

    if (errors?.fileSizeExceed === "single") {
      message.error("File size exceeds 5 MB.");
    } else if (errors?.fileSizeExceed === "total") {
      message.error("The total size of all files must not exceed 10 MB.");
    } else if (errors?.fileCountExceed) {
      message.error(
        "You cannot only upload one attachment so upload accordingly.",
      );
    } else if (
      natureOfExpenseSelectedValue !== "Travel Settlement" &&
      totalReimbursement.indexOf("-") !== -1
    ) {
      message.error(
        totalReimbursement +
          " amount is with you. If you have any concern then please contact with Finance Department!!",
      );
    } else if (isBackDated) {
      const messageKey = "publishDraftExpenseError";
      setHasError(true);
      message.open({
        key: messageKey,
        type: "error",
        duration: 0, // stay until closed
        content: (
          <div className="customMsgPopup">
            <span>
              Window Closed! Backdated entry is not allowed. Please update its
              Expense Period and try again.
            </span>
            <Button
              type="link"
              size="small"
              onClick={() => {
                message.destroy(messageKey);
                setHasError(false);
              }}
            >
              X
            </Button>
          </div>
        ),
      });
    } else {
      setAddExpenseValues(values);
      setIsModalVisible(true);
    }
  };

  const handleOk = () => {
    if (!changedExpenseValues) return;
    setIsModalVisible(false);
    setShowLoader(true);
    setErrors({ fileSizeExceed: "", fileCountExceed: false });

    const rowsData = changedExpenseValues?.expensesDetails?.map((detail) => ({
      date: detail.date,
      description: detail.description,
      departmentID: detail.departmentID?._id || detail.departmentID,
      expenseCodeID: detail.expenseCodeID?._id || detail.expenseCodeID,
      currencyID: detail.currencyID?._id || detail.currencyID,
      cost: detail.cost,
      recharge: detail.recharge,
      mediaInfo: detail.mediaInfo,
      mediaInfoLength: detail?.mediaInfo?.length || 0,
    }));

    const payload = {
      action: "publishSingle",
      ids: id,
      newFileCount,
      empName: storeEmpName,
      empID: parseInt(storeEmpID),
      departmentID: changedExpenseValues?.departmentID,
      expensePeriodFromDate: changedExpenseValues?.expensePeriodFromDate,
      expensePeriodToDate: changedExpenseValues?.expensePeriodToDate,
      businessPurpose: changedExpenseValues?.businessPurpose,
      natureOfExpense: changedExpenseValues?.natureOfExpense,
      projectCodeID:
        changedExpenseValues?.projectCodeID?._id ||
        changedExpenseValues?.projectCodeID,
      expensesDetails: rowsData,
      historySource: "portal",
      historyAction:
        formAction === "draft" ? "save as draft" : "Publishing drafted expense",
      historyUserID: storeLIUId,
      status:
        formAction === "draft"
          ? process.env.REACT_APP_DRAFT_STATUS.toUpperCase()
          : process.env.REACT_APP_PENDING_STATUS.toUpperCase(),
      isDrafted: process.env.REACT_APP_DRAFT_STATUS.toUpperCase(),
    };

    if (adminApprovers.length > 0) {
      if (changedExpenseValues?.empID === storeEmpID) {
        if (
          changedExpenseValues?.approvers &&
          changedExpenseValues?.approvers.length >= 1
        ) {
          payload.adminSelectedApprover = changedExpenseValues?.approvers?.[0]
            ?.userID
            ? changedExpenseValues?.approvers?.[0]?.userID
            : changedExpenseValues?.approvers || "";
        }
      }
    }

    if (changedExpenseValues?.natureOfExpense === "Travel Settlement") {
      payload.travelSettlement = {
        fromDate: changedExpenseValues.travelSettlementFromDate,
        toDate: changedExpenseValues.travelSettlementToDate,
        location: changedExpenseValues.location?.value,
        numberOfDays: changedExpenseValues.numberOfDays,
        perDiem: changedExpenseValues.perDiem,
        exchangeRate: changedExpenseValues.exchangeRate,
        totalForeignAmount: changedExpenseValues.totalForeignAmount,
        totalINRAmount: changedExpenseValues.totalINRAmount,
        advanceAmount: changedExpenseValues.advanceAmount || null,
        travelSettleMediaInfo: changedExpenseValues.travelSettleMediaInfo || [],
        travelSettleMediaInfoLength:
          changedExpenseValues?.travelSettleMediaInfo?.length || 0,
      };
    }

    dispatch(publishExpenseThunk(payload))
      .then(() => {
        message.success(
          formAction === process.env.REACT_APP_DRAFT_STATUS.toLowerCase()
            ? "Expense drafted successfully."
            : "Expense published successfully.",
        );

        setShowLoader(false);

        // Notify parent to refresh list
        if (onCloseReset) onCloseReset();

        navigate(`/${process.env.REACT_APP_DASHBOARD_EL_SEARCH_IN}`);
      })
      .catch((error) => {
        setShowLoader(false);
      });
  };

  //Rows operations
  const handleAddRow = () => {
    const newIndex = rows.length + 1;
    setRows([
      ...rows,
      {
        title: `Expense Detail #${newIndex}`,
        date: "",
        description: "",
        departmentID: "",
        expenseCodeID: "",
        currencyID: "",
        cost: "",
        mediaInfo: [],
      },
    ]);
  };

  const handleRemoveRow = (indexToRemove) => {
    const updatedRows =
      rows && rows.filter((row, index) => index !== indexToRemove);
    setRows(updatedRows);
    // Convert date fields back to moment objects if they exist
    const updatedFields =
      updatedRows &&
      updatedRows?.map((row) => ({
        ...row,
        date: row.date ? moment(row.date) : null,
      }));
    form.setFieldsValue({ rows: updatedFields });
  };

  const handleChange = (index, field, value) => {
    const updatedRows = [...rows];

    if (
      field === "departmentID" ||
      field === "expenseCodeID" ||
      field === "currencyID"
    ) {
      updatedRows[index][field] = value;
    } else {
      updatedRows[index][field] = value;
    }

    //updatedRows[index][field] = value;

    //setRows(updatedRows);

    const currentRow = updatedRows[index];
    const { date, currencyID, cost } = currentRow;

    const costValue = parseFloat(cost || 0);

    //  Only process if both date and currency are selected
    if (date && currencyID) {
      const matched = getRateByDate(currencyRates, currencyID?._id, date);
      const rate = matched?.rate || 1;
      const rateDateTime = matched?.effectiveDateTime || null;

      updatedRows[index].currencyRate = rate;
      //updatedRows[index].currencyRateDateTime = rateDateTime;
      updatedRows[index].costInINR = (costValue * rate).toFixed(2);
    } else {
      // If not all values selected, just default costInINR = cost
      const rate = currencyRates[currencyID]?.[0]?.rate || 1;
      updatedRows[index].costInINR = (costValue * rate).toFixed(2);
    }

    // //  Recalculate total reimbursement
    const total = updatedRows.reduce((sum, r) => {
      const val = parseFloat(r.costInINR || 0);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);

    setRows(updatedRows);
    tableTotalRef.current = total;

    if (changedExpenseValues?.natureOfExpense === "Travel Settlement") {
      const travelINR = Number(
        changedExpenseValues?.totalINRAmount ||
          changedExpenseValues?.travelSettlement?.[0]?.totalINRAmount ||
          0,
      );
    }

    // Update the mediaInfo in the specific detail
    const updatedDetails = (updatedRows || [])?.map((item, idx) => {
      if (idx === index) {
        return {
          ...item,
          [field]: updatedRows[index][field],
        };
      }
      return item;
    });

    // // Update the state or parent component's state with the updated savedExpenseDetail
    setChangedExpenseValues((prevState) => ({
      ...prevState,
      expensesDetails: updatedDetails,
    }));
  };

  // Function to remove media
  const handleMediaRemove = (detail, index) => {
    // Show confirmation modal
    Modal.confirm({
      title: "Confirm Removal",
      content: "Are you sure you want to remove this media item?",
      okText: "Yes",
      cancelText: "No",
      className: "revokeOrDeleteConfirmModal",

      okButtonProps: {
        className: "defaultbutton",
      },
      cancelButtonProps: {
        className: "defaultWhiteBtn",
      },
      onOk: () => {
        // Create a copy of the mediaInfo array for the respective detail
        let updatedMediaInfo = [...detail.mediaInfo];
        // Remove the selected file from the array (e.g., removing the first item for simplicity)
        updatedMediaInfo.splice(0, 1); // You can adjust the index based on your logic

        // Check if mediaInfo is empty after removal and set it explicitly to an empty array
        if (updatedMediaInfo.length === 0) {
          updatedMediaInfo = []; // Set to empty array if no files are left
          //updateRowFiles(index, fileSources, updatedRows);
        }
        // Update the mediaInfo in the specific detail
        const updatedDetails = changedExpenseValues?.expensesDetails?.map(
          (item, idx) => {
            if (idx === index) {
              return {
                ...item,
                mediaInfo: updatedMediaInfo,
              };
            }
            return item;
          },
        );

        // Update the state or parent component's state with the updated savedExpenseDetail
        setChangedExpenseValues((prevState) => ({
          ...prevState,
          expenseValues: updatedDetails,
        }));
        setRows((prevRows) =>
          prevRows?.map((row, idx) => {
            if (idx === index) {
              return {
                ...row,
                mediaInfo: updatedMediaInfo, // Update mediaInfo for the specific row
              };
            }
            return row; // Keep other rows unchanged
          }),
        );
        setMediaItem((prevState) => ({
          ...prevState,
          ["item_" + index]: "removed",
        }));

        setNewFileCount(newFileCount - 1);
      },
      onCancel: () => {
        // Optionally, handle cancellation logic here
        console.log("Removal cancelled");
      },
    });
  };

  const handleFileChange = (index, fileOrEvent, onSuccess) => {
    // Handle both Dragger (file object) and Browse File (event)
    const files = fileOrEvent?.target?.files || [fileOrEvent]; // auto-detect source

    if (files.length > 1) {
      setErrors({ fileCountExceed: true });
      message.error("Only one file can be uploaded.");
      return;
    }

    setErrors({ fileCountExceed: false });

    const maxSingleFileSize = 4 * 1024 * 1024; // 4MB
    const file = files[0];
    const updatedRows = [...rows];

    // File size check
    if (file.size > maxSingleFileSize) {
      setErrors({ fileSizeExceed: "single" });
      message.error(`File "${file.name}" exceeds 4MB.`);
      return;
    }

    // File type check
    const allowedTypes = ["image/png", "image/jpeg", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      message.error("Only JPG, PNG, or PDF files are allowed!");
      return;
    }

    const reader = new FileReader();
    const fileSources = [];

    const arrayBufferToBase64 = (buffer) => {
      let binary = "";
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return window.btoa(binary);
    };

    reader.onload = (e) => {
      let fileData;
      if (file.type.startsWith("image/")) {
        fileData = e.target.result;
      } else {
        const base64Data = arrayBufferToBase64(e.target.result);
        fileData = `data:${file.type};base64,${base64Data}`;
      }

      fileSources.push({
        src: fileData,
        name: file.name,
        type: file.type,
      });

      setChangedExpenseValues((prev) => ({
        ...prev,
        mediaInfo: fileData,
      }));

      form.setFieldsValue({
        mediaInfo: fileData,
      });
      updateRowFiles(index, fileSources, updatedRows);
      onSuccess("ok");
    };

    // Read file accordingly
    if (file.type.startsWith("image/")) {
      reader.readAsDataURL(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleImageClick = (filePath) => {
    const index = images.findIndex((img) => img === filePath);
    if (index !== -1) {
      setPhotoIndex(index);
      setIsOpen(true);
    }
  };

  const handleTravelMediaChange = (file, onSuccess) => {
    if (!file) return;

    const maxSingleFileSize = 4 * 1024 * 1024;
    if (file.size > maxSingleFileSize) {
      message.error("File exceeds 4MB.");
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      const media = [
        {
          src: e.target.result,
          name: file.name,
          type: file.type,
        },
      ];

      setChangedExpenseValues((prev) => ({
        ...prev,
        travelSettleMediaInfo: media,
      }));

      form.setFieldsValue({
        travelSettleMediaInfo: media,
      });

      setTravelMediaItem("freshUpload");
      onSuccess("ok");
    };

    if (file.type.startsWith("image/")) {
      reader.readAsDataURL(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const handleTravelMediaRemove = (fileIndex) => {
    Modal.confirm({
      title: "Confirm Removal",
      content: "Are you sure you want to remove this attachment?",
      okText: "Yes",
      cancelText: "No",
      className: "revokeOrDeleteConfirmModal",

      okButtonProps: {
        className: "defaultbutton",
      },
      cancelButtonProps: {
        className: "defaultWhiteBtn",
      },
      onOk: () => {
        setChangedExpenseValues((prev) => {
          const updatedSettlement = [...prev.travelSettlement];

          const updatedMedia = [
            ...(updatedSettlement[0]?.travelSettleMediaInfo || []),
          ];

          // Remove selected file
          updatedMedia.splice(fileIndex, 1);

          updatedSettlement[0] = {
            ...updatedSettlement[0],
            travelSettleMediaInfo: updatedMedia,
          };

          return {
            ...prev,
            travelSettlement: updatedSettlement,
          };
        });

        form.setFieldsValue({
          travelSettleMediaInfo: [],
        });

        setTravelMediaItem("removed");
      },
    });
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------
  useEffect(() => {
    //Fetch Draft Expense's details
    const fetchAll = async () => {
      try {
        setShowLoader(true);

        const promises = [
          dispatch(
            getListOfExpenseDetailThunk({ route: currentPath, id }),
          ).unwrap(),
          dispatch(getCurrencyListThunk()).unwrap(),
          dispatch(getProjectCodeListThunk()).unwrap(),
          dispatch(getExpenseCodeListThunk()).unwrap(),
          dispatch(getDepartmentListThunk()).unwrap(),
          dispatch(getVendorListThunk()).unwrap(),
          dispatch(getUserListWithDetailThunk()).unwrap(),
          dispatch(getDiemListThunk()).unwrap(),
          dispatch(getLocationListThunk()).unwrap(),
          dispatch(
            getBlockedDateThunk({
              role: storeRole,
              lIUId: storeLIUId,
            }),
          ).unwrap(),
          dispatch(getCurrencyListDetailThunk()).unwrap(),
        ];

        if (storeRole === process.env.REACT_APP_ADROLE) {
          promises.push(
            dispatch(
              getAdminApproverListThunk({
                category: process.env.REACT_APP_EXPENSE_CATEGORY_CAPEX,
              }),
            ).unwrap(),
          );
        } else {
          promises.push(Promise.resolve([]));
        }

        const [
          detailResponse,
          currencies,
          projectCodes,
          expenseCodes,
          departments,
          vendors,
          users,
          diems,
          locations,
          expCal,
          currenciesDetails,
          adminApprovers,
        ] = await Promise.all(promises);

        // -------- PRIMARY DATA --------
        setSavedExpenseDetail(detailResponse); //To show prefilled form values
        setNatureOfExpenseSelectedValue(detailResponse?.natureOfExpense);
        setRows(
          detailResponse?.expensesDetails?.map((d) => ({
            date: d.date || "",
            description: d.description || "",
            departmentID: d.departmentID?._id || "",
            expenseCodeID: d.expenseCodeID?._id || null,
            currencyID:
              d.currencyID?._id || d.currencyID?.value || d.currencyID || "",
            cost: d.cost || "",
            costInINR: Number(d.costInINR || d.cost || 0),
            mediaInfo: d.mediaInfo || [],
          })) || [],
        );
        setChangedExpenseValues(detailResponse);

        // -------- MASTER DATA --------
        setAllCurrencies(currencies?.currencyDropdown || []);
        setCurrencyRates(currencies?.currencyRates || []);
        setProjectCodes(projectCodes || []);
        setExpenseCodes(expenseCodes || []);
        setDepartments(departments || []);
        setVendors(vendors || []);
        setUsersDetailList(users || []);
        setCurrenciesDetailList(currenciesDetails || []);
        setDiems(diems || []);
        setLocations(locations || []);
        setCurrenciesInfo(currencies?.list || []);
        if (adminApprovers) setAdminApprovers(adminApprovers);

        // -------- BLOCKED DATE --------
        if (
          expCal?.expCalRes?.blockedOn &&
          expCal?.expCalRes?.status === process.env.REACT_APP_BLOCK_STATUS
        ) {
          setBlockedDate(expCal.expCalRes);
        } else {
          setBlockedDate(null);
        }
      } catch (error) {
        console.error("Error loading expense data:", error);
      } finally {
        setShowLoader(false);
      }
    };

    fetchAll();
  }, [dispatch, currentPath, id]);

  //Form value setting fetching form savedExpenseDetail's state
  useEffect(() => {
    //Any changes made to Expense's detail
    if (!savedExpenseDetail || showLoader) return;

    form.setFieldsValue({
      empID: savedExpenseDetail?.empID,
      empName: savedExpenseDetail?.empName,
      departmentID: `${storeEmpDepartmentName} - ${storeEmpDepartmentCode}`,
      expensePeriodFromDate: [
        dayjs(savedExpenseDetail.expensePeriodFromDate, "YYYY-MM-DD"),
        dayjs(savedExpenseDetail.expensePeriodToDate, "YYYY-MM-DD"),
      ],
      projectCodeID: savedExpenseDetail?.projectCodeID
        ? savedExpenseDetail?.projectCodeID
        : getProjectCodeLabel(
            savedExpenseDetail?.projectCodeID,
            projectCodes,
          ) || "",
      natureOfExpense: savedExpenseDetail?.natureOfExpense,
      businessPurpose: savedExpenseDetail?.businessPurpose,
    });
    if (savedExpenseDetail?.empID === storeEmpID) {
      if (
        savedExpenseDetail?.approvers &&
        savedExpenseDetail?.approvers.length >= 1
      ) {
        const approverInfo = usersDetailList.find(
          (userItem) =>
            userItem.id === savedExpenseDetail?.approvers?.[0]?.userID,
        );
        form.setFieldsValue({
          approvers: approverInfo?.empName || "",
        });
      }
    }

    if (savedExpenseDetail?.expensesDetails) {
      const rowsData = savedExpenseDetail.expensesDetails?.map((detail) => ({
        date: detail.date ? dayjs(detail.date, "YYYY-MM-DD") : null,
        description: detail.description || "",
        departmentID:
          getDepartmentLabel(detail.departmentID, departments) || "",
        expenseCodeID:
          getExpenseCodeLabel(detail.expenseCodeID, expenseCodes) || null,
        currencyID:
          getCurrencyCode(detail.currencyID, currenciesDetailList) || "",
        recharge: detail.recharge || "",
        cost: detail.cost || "",
        mediaInfo: detail.mediaInfo || [],
      }));

      form.setFieldsValue({
        rows: rowsData,
      });
    }

    const ts = savedExpenseDetail?.travelSettlement?.[0];
    if (ts) {
      form.setFieldsValue({
        travelSettlement: [
          {
            period: [
              dayjs(ts.fromDate, "YYYY-MM-DD"),
              dayjs(ts.toDate, "YYYY-MM-DD"),
            ],
            location: ts.location,
            numberOfDays: ts.numberOfDays,
            perDiem: ts.perDiem,
            totalForeignAmount: ts.totalForeignAmount,
            totalINRAmount: ts.totalINRAmount,
            advanceAmount: ts.advanceAmount,
            travelSettleMediaInfo: ts.mediaInfo || [],
          },
        ],
      });
    }
  }, [savedExpenseDetail, showLoader]);

  //calculating for Total Reimbursement
  useEffect(() => {
    const rowsTotal = rows.reduce((sum, r) => {
      try {
        const cost = Number(r.cost || 0);
        if (!cost) return sum;

        const currencyId =
          r.currencyID?._id || r.currencyID?.value || r.currencyID;

        const date =
          typeof r.date === "string" ? r.date : r.date?.format?.("YYYY-MM-DD");

        // If currency/date missing → treat as INR
        if (!currencyId || !date) {
          return sum + cost; // ❗ DO NOT count yet
        }

        const matched = getRateByDate(currencyRates, currencyId, date);
        const rate = matched?.rate || 1;

        return sum + cost * rate;
      } catch (e) {
        console.error("Row calculation failed:", e, r);
        return sum;
      }
    }, 0);

    const isTravelSettlement =
      natureOfExpenseSelectedValue === "Travel Settlement";

    const travelINR = isTravelSettlement
      ? Number(changedExpenseValues?.totalINRAmount || 0)
      : 0;

    const advance = isTravelSettlement
      ? Number(changedExpenseValues?.advanceAmount || 0)
      : 0;

    const finalTotal = (rowsTotal + travelINR - advance).toFixed(2);

    setTotalReimbursement(finalTotal);
  }, [
    rows,
    natureOfExpenseSelectedValue,
    changedExpenseValues?.totalINRAmount,
    changedExpenseValues?.advanceAmount,
    currencyRates,
  ]);

  //Any changes made to Travel Settlement's fields
  useEffect(() => {
    //Need to cross check
    if (
      changedExpenseValues?.travelSettlement?.length &&
      !backendTravelRef.current
    ) {
      backendTravelRef.current = {
        ...changedExpenseValues.travelSettlement?.[0],
      };
    }
    if (
      changedExpenseValues?.travelSettlement?.[0]?.location &&
      !changedExpenseValues?.location
    ) {
      setChangedExpenseValues((prev) => ({
        ...prev,
        location: prev.travelSettlement?.[0].location,
      }));
    }
  }, [changedExpenseValues?.travelSettlement]);

  //Any changes made to natureOfExpense and travelSettlement
  useEffect(() => {
    if (
      !changedExpenseValues?.travelSettlement?.length ||
      changedExpenseValues?.natureOfExpense !== "Travel Settlement"
    )
      return;

    const ts = changedExpenseValues.travelSettlement?.[0];

    form.setFieldsValue({
      travelSettlement: [
        {
          period: [
            dayjs(ts.fromDate, "YYYY-MM-DD"),
            dayjs(ts.toDate, "YYYY-MM-DD"),
          ],
          location: ts.location,
          numberOfDays: ts.numberOfDays,
          perDiem: ts.perDiem,
          totalForeignAmount: ts.totalForeignAmount,
          totalINRAmount: ts.totalINRAmount,
          advanceAmount: ts.advanceAmount,
          travelSettleMediaInfo: ts.travelSettleMediaInfo || [],
        },
      ],
    });
  }, [
    changedExpenseValues?.travelSettlement,
    changedExpenseValues?.natureOfExpense,
  ]);

  // Travel Settlement → Total Reimbursement sync
  useEffect(() => {
    if (changedExpenseValues?.natureOfExpense !== "Travel Settlement") return;
    const fromDate =
      changedExpenseValues?.travelSettlementFromDate ??
      changedExpenseValues?.travelSettlement?.[0]?.fromDate;

    const toDate =
      changedExpenseValues?.travelSettlementToDate ??
      changedExpenseValues?.travelSettlement?.[0]?.toDate;

    if (!fromDate || !toDate || !effectiveLocation) return;

    if (!locationObj) return;

    const currency = locationObj.label?.split("-")?.[1]?.trim();
    if (!currency) return;

    const days = moment(toDate).diff(moment(fromDate), "days") + 1;

    const perDiem = PER_DIEM_RATES?.[currency] || 0;

    const exchangeRate = getExchangeRateToINR(
      currency,
      currenciesInfo,
      fromDate,
    );

    const totalForeignAmount = days * perDiem;
    const totalINRAmount = totalForeignAmount * exchangeRate;

    const advanceAmount =
      changedExpenseValues?.advanceAmount ??
      changedExpenseValues?.travelSettlement?.[0]?.advanceAmount;

    //  UPDATE REACT STATE
    setChangedExpenseValues((prev) => ({
      ...prev,
      numberOfDays: days,
      perDiem,
      exchangeRate,
      totalForeignAmount,
      totalINRAmount,
      advanceAmount,
    }));

    const existingTS = {
      ...(form.getFieldValue(["travelSettlement", 0]) || {}),
      location: changedExpenseValues.location, // FORCE latest location
    };

    form.setFieldsValue({
      travelSettlement: [
        {
          ...existingTS, // preserves period & location
          location: effectiveLocation,
          numberOfDays: days,
          perDiem,
          exchangeRate,
          totalForeignAmount: totalForeignAmount.toFixed(2),
          totalINRAmount: totalINRAmount.toFixed(2),
          //advanceAmount: changedExpenseValues.advanceAmount,
          //travelSettleMediaInfo: changedExpenseValues.travelSettleMediaInfo || []
        },
      ],
    });
  }, [
    changedExpenseValues?.natureOfExpense,
    changedExpenseValues?.travelSettlementFromDate,
    changedExpenseValues?.travelSettlementToDate,
    changedExpenseValues?.travelSettlement?.[0]?.fromDate,
    changedExpenseValues?.travelSettlement?.[0]?.toDate,
    changedExpenseValues?.location,
    locations,
    currenciesInfo,
  ]);

  //Publish
  useEffect(() => {
    if (isPublishIconClick && !hasExecuted.current) {
      hasExecuted.current = true; // mark as executed
      // prevent repeated execution
      setShowLoader(true);
      const endpoint = `/publishMulExpense`;
      const method = "PUT";

      const payload = {
        action: "publishMul",
        ids: [id],
        historySource: "listing-page",
        historyAction: "Publishing drafted expense",
        historyUserID: storeLIUId,
        status: process.env.REACT_APP_PENDING_STATUS.toUpperCase(),
      };
      const messageKey = "publishDraftExpenseError";

      APICaller(
        storeRole,
        storeToken,
        endpoint,
        method,
        payload,
        storeAccessTokenExpiresOn,
      )
        .then((response) => {
          setShowLoader(false);

          if (response.statusCode) {
            if (response.message) {
              setHasError(true);
              message.open({
                key: messageKey,
                type: response.statusCode === 400 ? "error" : "success",
                duration: 0, // stay until closed
                content: (
                  <div className="customMsgPopup">
                    <span>{response.message}</span>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => {
                        message.destroy(messageKey);
                        setHasError(false);
                      }}
                    >
                      X
                    </Button>
                  </div>
                ),
              });
            } else {
              message.success("Expense published successfully.");
              navigate(`/${process.env.REACT_APP_DASHBOARD_EL_SEARCH_IN}`);
            }
          }
        })
        .catch((error) => {
          setShowLoader(false);
          message.error("Something went wrong!");
        });
      // Optional: clear the location.state so it won't re-trigger on back
      window.history.replaceState({}, document.title);
    }
  }, [isPublishIconClick]);

  //Any changes made to natureOfExpense if it is Travel Settlement
  // useEffect(() => {   //Need to cross check
  //   if (changedExpenseValues?.natureOfExpense === "Travel Settlement") {
  //     form.setFieldsValue({
  //       travelSettlement: [
  //         {
  //           numberOfDays: 0,
  //           perDiem: 0,
  //           totalForeignAmount: 0,
  //           totalINRAmount: "INR 0",
  //         },
  //       ],
  //     });
  //   }
  // }, [changedExpenseValues?.natureOfExpense]);

  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <>
      {isVisible && (
        <div className="addExpensePopupForm">
          <div
            className="addExpensePopupFormWraper"
            ref={wrapperRef}
            onScroll={handleScroll}
          >
            {showLoader ? (
              <CustomLoader showLoader={showLoader} />
            ) : (
              <>
                <div className="expenseDetailTitle contentBottomPadding">
                  <h3 className="text-dark font-size-lg">Add New Expense</h3>

                  <button
                    onClick={() => setIsVisible(false)}
                    className="expenseDetailTitleClose"
                  >
                    <img src={media.popupCross.svg} alt="close" />
                  </button>
                </div>
                <div className="customExpenseLabelForm">
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    className="form-validation-start"
                  >
                    <div style={{ padding: "0 9px" }}>
                      <Row className="addMainWrap" gutter={[18, 24]}>
                        {/* Employee ID */}
                        <Col xs={24} md={12} xl={12}>
                          <Form.Item
                            className="m-0"
                            name="empID"
                            rules={[
                              {
                                pattern: /^[0-9]*\.?[0-9]+$/,
                                message:
                                  "Alpbhabets and special symbols not allowing in Employee ID",
                              },
                            ]}
                          >
                            <div className="form-row-wrap">
                              <label htmlFor="empID">Employee ID</label>
                              <Form.Item name="empID" noStyle>
                                <Input
                                  id="empID"
                                  className="form-control h-45px rounded-md"
                                  placeholder="Employee ID"
                                  disabled={changedExpenseValues?.empID !== 1}
                                />
                              </Form.Item>
                            </div>{" "}
                          </Form.Item>
                        </Col>

                        {/* Employee Name */}
                        <Col xs={24} md={12} xl={12}>
                          <Form.Item
                            className="m-0"
                            name="empName"
                            rules={[
                              {
                                pattern: /^[^\d]*$/,
                                message:
                                  "Numbers are not allowed in the employee name",
                              },
                            ]}
                          >
                            <div className="form-row-wrap">
                              <label htmlFor="empName">Employee Name</label>
                              <Form.Item name="empName" noStyle>
                                <Input
                                  id="empName"
                                  className="form-control h-45px rounded-md"
                                  placeholder="Employee Name"
                                  disabled
                                />
                              </Form.Item>
                            </div>
                          </Form.Item>
                        </Col>

                        {/* Department */}
                        <Col xs={24} md={12} xl={12}>
                          <Form.Item className="m-0" name="departmentID">
                            <div className="form-row-wrap">
                              <label htmlFor="departmentID">Department</label>
                              <Form.Item name="departmentID" noStyle>
                                <Input
                                  id="departmentID"
                                  placeholder="Select Department"
                                  className="form-control h-45px rounded-md"
                                  disabled="disabled"
                                />
                              </Form.Item>
                            </div>
                          </Form.Item>
                        </Col>

                        {/* Expense Period: (From - To) */}
                        <Col xs={24} md={12} xl={12}>
                          <Form.Item
                            className="m-0"
                            name="expensePeriodFromDate"
                            rules={[
                              {
                                required: true,
                                message:
                                  "Please enter Expense Period: (From - To)",
                              },
                            ]}
                          >
                            <div className="form-row-wrap">
                              <label htmlFor="expensePeriodFromDate">
                                Expense Period: (From - To){" "}
                                <span className="mandatory-symbol">*</span>
                              </label>
                              <Form.Item name="expensePeriodFromDate" noStyle>
                                <RangePicker
                                  className={`form-control h-45px rounded-md d-inline-flex drftRP ${
                                    hasError ? "hasError" : ""
                                  }`}
                                  format="DD-MM-YYYY"
                                  disabledDate={(current) => {
                                    const minDate = new Date(
                                      blockedDate?.blockedOn,
                                    );
                                    return (
                                      current &&
                                      current.toDate() <= getMinDate(minDate)
                                    );
                                  }}
                                  onChange={(date, dates) => {
                                    const formattedDates =
                                      dates &&
                                      dates?.map((date) =>
                                        moment(date, "DD-MM-YYYY").format(
                                          "YYYY-MM-DD",
                                        ),
                                      );

                                    if (
                                      formattedDates &&
                                      formattedDates[0] &&
                                      formattedDates[1]
                                    ) {
                                      setChangedExpenseValues({
                                        ...changedExpenseValues,
                                        expensePeriodFromDate:
                                          formattedDates[0],
                                        expensePeriodToDate: formattedDates[1],
                                      });
                                    }
                                  }}
                                />
                              </Form.Item>
                            </div>
                          </Form.Item>
                        </Col>

                        {/* Project Code */}
                        <Col xs={24} md={12} xl={12}>
                          <Form.Item
                            className="m-0"
                            name="projectCodeID"
                            rules={[
                              {
                                validator: (_, value) => {
                                  if (value !== undefined && value !== null) {
                                    return Promise.resolve(); // valid
                                  }
                                  if (value === null) {
                                    return Promise.resolve(); // allow 'N/A' case
                                  }
                                  return Promise.reject(
                                    "Please select project code",
                                  );
                                },
                              },
                            ]}
                          >
                            <div className="form-row-wrap">
                              <label htmlFor="projectCodeID">
                                Project Code
                                <span className="mandatory-symbol">*</span>
                              </label>
                              <Form.Item name="projectCodeID" noStyle>
                                <Select
                                  id="projectCodeID"
                                  showSearch
                                  className="h-45px lh-1Pt5 rounded-md"
                                  options={projectCodes}
                                  onChange={(value) => {
                                    setChangedExpenseValues({
                                      ...changedExpenseValues,
                                      projectCodeID: value,
                                    });
                                  }}
                                  filterOption={(input, option) =>
                                    option?.label
                                      ?.toLowerCase()
                                      .includes(input.toLowerCase())
                                  }
                                />
                              </Form.Item>
                            </div>
                          </Form.Item>
                        </Col>

                        {/* Approvers field get visible When there is admin role*/}
                        {storeRole === process.env.REACT_APP_ADROLE && (
                          <Col xs={24} md={12} xl={12}>
                            <Form.Item
                              className="m-0"
                              name="approvers"
                              rules={[
                                {
                                  required: true,
                                  message: "Please select approver",
                                },
                              ]}
                            >
                              <div className="form-row-wrap">
                                <label htmlFor="approvers">Approvers</label>
                                <Form.Item name="approvers" noStyle>
                                  <Select
                                    id="approvers"
                                    showSearch
                                    className="h-45px lh-1Pt5 rounded-md"
                                    allowClear
                                    options={adminApprovers}
                                    onChange={(value) => {
                                      setChangedExpenseValues({
                                        ...changedExpenseValues,
                                        approvers: value,
                                      });
                                    }}
                                    filterOption={(input, option) =>
                                      option?.label
                                        ?.toLowerCase()
                                        .includes(input.toLowerCase())
                                    }
                                  />
                                </Form.Item>
                              </div>
                            </Form.Item>
                          </Col>
                        )}

                        {/* Nature of Expense */}
                        <Col xs={24} md={12} xl={12}>
                          <Form.Item
                            className="m-0"
                            name="natureOfExpense"
                            rules={[
                              {
                                required: true,
                                message: "Nature of Expense is Mandatory",
                              },
                            ]}
                          >
                            <div className="form-row-wrap">
                              <label htmlFor="natureOfExpense">
                                Nature of Expense
                                <span className="mandatory-symbol">*</span>
                              </label>
                              <Form.Item name="natureOfExpense" noStyle>
                                <Select
                                  id="natureOfExpense"
                                  showSearch
                                  className="h-45px lh-1Pt5 rounded-md"
                                  allowClear
                                  options={natureOfExpense}
                                  onChange={(value) => {
                                    setChangedExpenseValues({
                                      ...changedExpenseValues,
                                      natureOfExpense: value,
                                    });
                                  }}
                                  filterOption={(input, option) =>
                                    option?.label
                                      ?.toLowerCase()
                                      .includes(input.toLowerCase())
                                  }
                                />
                              </Form.Item>
                            </div>
                          </Form.Item>
                        </Col>

                        {/* Vendor Name */}
                        {changedExpenseValues?.natureOfExpense !==
                          "Travel Settlement" && (
                          <Col xs={24} md={12} xl={12}>
                            <Form.Item
                              className="m-0"
                              name="businessPurpose"
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter vendor name",
                                },
                              ]}
                            >
                              <div className="form-row-wrap">
                                <label htmlFor="businessPurpose">
                                  Vendor Name
                                  <span className="mandatory-symbol">*</span>
                                </label>
                                <Form.Item name="businessPurpose" noStyle>
                                  <Select
                                    id="businessPurpose"
                                    showSearch
                                    className="h-45px lh-1Pt5 rounded-md"
                                    allowClear
                                    options={vendors}
                                    onChange={(value) => {
                                      setChangedExpenseValues({
                                        ...changedExpenseValues,
                                        businessPurpose: value,
                                      });
                                    }}
                                    filterOption={(input, option) =>
                                      option?.label
                                        ?.toLowerCase()
                                        .includes(input.toLowerCase())
                                    }
                                  />
                                </Form.Item>
                              </div>
                            </Form.Item>
                          </Col>
                        )}
                      </Row>
                    </div>
                    {/* Travel Expense Section start here */}
                    {changedExpenseValues?.natureOfExpense ===
                      "Travel Settlement" && (
                      <>
                        {/* <div className="travelSettlement viewTravelSettlement">
                          <h3>Travel Settlement</h3>
                          <Row
                            className="travelSettlementRow"
                            gutter={[18, 24]}
                          >
                            <Col xs={24} md={12} xl={12}>
                              <div className="form-row-wrap">
                                <label name="fromDate">
                                  Travel Settlement Period: (From - To)
                                  <span className="mandatory-symbol">*</span>
                                </label>
                                <Form.Item
                                  name={["travelSettlement", 0, "period"]}
                                  rules={[
                                          {
                                            required: changedExpenseValues?.natureOfExpense === "Travel Settlement",
                                            message: "Please select Travel Settlement period",
                                          },
                                        ]}
                                  noStyle
                                >
                                  <RangePicker
                                    format="DD-MM-YYYY"
                                    disabledDate={(current) => {
                                      const minDate = new Date(
                                        blockedDate?.blockedOn
                                      );
                                      return (
                                        current &&
                                        current.toDate() <= getMinDate(minDate)
                                      );
                                    }}
                                    className="form-control rounded-md d-inline-flex"
                                    defaultValue={[
                                      changedExpenseValues?.travelSettlement?.[0]
                                        ?.fromDate
                                        ? dayjs(
                                            changedExpenseValues.travelSettlement[0]
                                              .fromDate,
                                            "YYYY-MM-DD"
                                          )
                                        : null,
                                      changedExpenseValues?.travelSettlement?.[0]
                                        ?.toDate
                                        ? dayjs(
                                            changedExpenseValues.travelSettlement[0]
                                              .toDate,
                                            "YYYY-MM-DD"
                                          )
                                        : null,
                                    ]}
                                    allowClear={true}
                                    onChange={(date, dates) => {
                                      const formattedDates =
                                        dates &&
                                        dates?.map((date) =>
                                          moment(date, "DD-MM-YYYY").format(
                                            "YYYY-MM-DD"
                                          )
                                        );

                                      if (
                                        formattedDates?.[0] &&
                                        formattedDates?.[1]
                                      ) {
                                        
                                        setChangedExpenseValues((prev) => ({
                                          ...prev,
                                          travelSettlementFromDate:
                                            formattedDates[0],
                                          travelSettlementToDate:
                                            formattedDates[1],
                                        }));
                                      }
                                      //setIsTravelInputsChanged(true);
                                      //setHasUserInteracted(true);
                                    }}
                                  />
                                </Form.Item>
                              </div>
                            </Col>
                            <Col xs={24} md={12} xl={12}>
                              <div className="form-row-wrap">
                                <label name="location">
                                  Location
                                  <span className="mandatory-symbol">*</span>
                                </label>
                                <Form.Item
                                  name={["travelSettlement", 0, "location"]}
                                  rules={[
                                          {
                                            required: changedExpenseValues?.natureOfExpense === "Travel Settlement",
                                            message: "Please select Location",
                                          },
                                        ]}
                                  noStyle
                                >
                                  <Select
                                    className="rounded-md"
                                    placeholder="Select Location"
                                    defaultValue={locations.find(
                                      (d) =>
                                        String(d.value) ===
                                        String(
                                          changedExpenseValues?.travelSettlement?.[0]
                                            .location
                                        )
                                    )}
                                    onChange={(value) => {                                      
                                      setChangedExpenseValues({
                                        ...changedExpenseValues,
                                        location: value,
                                      });
                                    }}
                                    options={locations}
                                  />
                                </Form.Item>
                              </div>
                            </Col>
                            <Col xs={24} md={6}>
                              <div className="form-row-wrap travelFieldsReadOnly">
                                <label name="numberOfDays">
                                  No. of Days
                                  <span className="mandatory-symbol">*</span>
                                </label>
                                <Form.Item
                                  name={["travelSettlement", 0, "numberOfDays"]}
                                  rules={[
                                          {
                                            required: changedExpenseValues?.natureOfExpense === "Travel Settlement",
                                            message: "No. of days is required",
                                          },
                                        ]}
                                  noStyle
                                >
                                  <Input
                                    placeholder="No. of Days"
                                    disabled
                                    className="form-control rounded-md"
                                  />

                                </Form.Item>
                              </div>
                            </Col>
                            <Col xs={24} md={6}>
                              <div className="form-row-wrap travelFieldsReadOnly">
                                <label name="perDiem">
                                  Per Diem
                                  <span className="mandatory-symbol">*</span>
                                </label>
                                <Form.Item
                                  name={["travelSettlement", 0, "perDiem"]}
                                  rules={[
                                          {
                                            required: changedExpenseValues?.natureOfExpense === "Travel Settlement",
                                            message: "Per Diem is required",
                                          },
                                        ]}
                                  noStyle
                                >
                                  <Input
                                    placeholder="Per Diem"
                                    disabled
                                    className="form-control rounded-md"
                                  />
                                  
                                </Form.Item>
                              </div>
                            </Col>
                            <Col xs={24} md={6}>
                              <div className="form-row-wrap travelFieldsReadOnly">
                                <label name="total">
                                  Total
                                  <span className="mandatory-symbol">*</span>
                                </label>
                                <Form.Item
                                  name={[
                                    "travelSettlement",
                                    0,
                                    "totalForeignAmount",
                                  ]}
                                  rules={[
                                          {
                                            required: changedExpenseValues?.natureOfExpense === "Travel Settlement",
                                            message: "Total is required",
                                          },
                                        ]}
                                  noStyle
                                >
                                  <Input
                                    placeholder="Total"
                                    disabled
                                    className="form-control rounded-md"
                                  />
                                  
                                </Form.Item>
                              </div>
                            </Col>
                            <Col xs={24} md={6}>
                              <div className="form-row-wrap travelFieldsReadOnly">
                                <label name="totalINRAmount">
                                  Total (INR)
                                  <span className="mandatory-symbol">*</span>
                                </label>
                                <Form.Item
                                  name={[
                                    "travelSettlement",
                                    0,
                                    "totalINRAmount",
                                  ]}
                                  rules={[
                                          {
                                            required: changedExpenseValues?.natureOfExpense === "Travel Settlement",
                                            message: "Total (INR) is required",
                                          },
                                        ]}
                                  noStyle
                                >
                                  <Input
                                    placeholder="Total (INR)"
                                    disabled
                                    className="form-control rounded-md"
                                  />
                                </Form.Item>
                              </div>
                            </Col>
                          </Row>
                        </div> */}
                        {changedExpenseValues?.natureOfExpense ===
                          "Travel Settlement" && (
                          <div className="travelSettlement viewTravelSettlement">
                            <h3>Travel Settlement</h3>

                            <Row
                              className="travelSettlementRow"
                              gutter={[18, 24]}
                            >
                              {/* ================= PERIOD ================= */}
                              <Col xs={24} md={12} xl={12}>
                                <div className="form-row-wrap">
                                  <label>
                                    Travel Settlement Period: (From - To)
                                    <span className="mandatory-symbol">*</span>
                                  </label>

                                  <Form.Item shouldUpdate noStyle>
                                    {() => {
                                      const errors = form.getFieldError([
                                        "travelSettlement",
                                        0,
                                        "period",
                                      ]);

                                      return (
                                        <>
                                          <Form.Item
                                            name={[
                                              "travelSettlement",
                                              0,
                                              "period",
                                            ]}
                                            rules={[
                                              {
                                                required: true,
                                                message:
                                                  "Please enter Travel Settlement Period: (From - To)",
                                              },
                                            ]}
                                            noStyle
                                          >
                                            <RangePicker
                                              format="DD-MM-YYYY"
                                              className={`form-control rounded-md d-inline-flex ${
                                                errors.length
                                                  ? "ant-input-status-error"
                                                  : ""
                                              }`}
                                              disabledDate={(current) => {
                                                const minDate = new Date(
                                                  blockedDate?.blockedOn,
                                                );
                                                return (
                                                  current &&
                                                  current.toDate() <=
                                                    getMinDate(minDate)
                                                );
                                              }}
                                              onChange={(date, dates) => {
                                                if (dates?.[0] && dates?.[1]) {
                                                  setChangedExpenseValues(
                                                    (prev) => ({
                                                      ...prev,
                                                      travelSettlementFromDate:
                                                        moment(
                                                          dates[0],
                                                          "DD-MM-YYYY",
                                                        ).format("YYYY-MM-DD"),
                                                      travelSettlementToDate:
                                                        moment(
                                                          dates[1],
                                                          "DD-MM-YYYY",
                                                        ).format("YYYY-MM-DD"),
                                                    }),
                                                  );
                                                }
                                              }}
                                            />
                                          </Form.Item>

                                          {errors.length > 0 && (
                                            <div className="ant-form-item-explain-error">
                                              {errors[0]}
                                            </div>
                                          )}
                                        </>
                                      );
                                    }}
                                  </Form.Item>
                                </div>
                              </Col>

                              {/* ================= LOCATION ================= */}
                              <Col xs={24} md={12} xl={12}>
                                <div className="form-row-wrap">
                                  <label>
                                    Location
                                    <span className="mandatory-symbol">*</span>
                                  </label>

                                  <Form.Item shouldUpdate noStyle>
                                    {() => {
                                      const errors = form.getFieldError([
                                        "travelSettlement",
                                        0,
                                        "location",
                                      ]);

                                      return (
                                        <>
                                          <Form.Item
                                            name={[
                                              "travelSettlement",
                                              0,
                                              "location",
                                            ]}
                                            rules={[
                                              {
                                                required: true,
                                                message:
                                                  "Please select Location",
                                              },
                                            ]}
                                            noStyle
                                          >
                                            <Select
                                              className={`rounded-md ${
                                                errors.length
                                                  ? "ant-input-status-error"
                                                  : ""
                                              }`}
                                              placeholder="Select..."
                                              options={locations}
                                              onChange={(value) =>
                                                setChangedExpenseValues(
                                                  (prev) => ({
                                                    ...prev,
                                                    location: value,
                                                  }),
                                                )
                                              }
                                            />
                                          </Form.Item>

                                          {errors.length > 0 && (
                                            <div className="ant-form-item-explain-error">
                                              {errors[0]}
                                            </div>
                                          )}
                                        </>
                                      );
                                    }}
                                  </Form.Item>
                                </div>
                              </Col>

                              {/* ================= READ-ONLY FIELDS ================= */}
                              {[
                                {
                                  label: "No. of Days",
                                  placeholder: "No. of Days",
                                  name: "numberOfDays",
                                  message: "No. of days is required",
                                },
                                {
                                  label: "Per Diem",
                                  placeholder: "Per Diem",
                                  name: "perDiem",
                                  message: "Per Diem is required",
                                },
                                {
                                  label: "Total",
                                  placeholder: "Total",
                                  name: "totalForeignAmount",
                                  message: "Total is required",
                                },
                                {
                                  label: "Total (INR)",
                                  placeholder: "Total (INR)",
                                  name: "totalINRAmount",
                                  message: "Total (INR) is required",
                                },
                              ]?.map((field) => (
                                <Col xs={24} md={6} key={field.name}>
                                  <div className="form-row-wrap travelFieldsReadOnly">
                                    <label>
                                      {field.label}
                                      <span className="mandatory-symbol">
                                        *
                                      </span>
                                    </label>

                                    <Form.Item shouldUpdate noStyle>
                                      {() => {
                                        const errors = form.getFieldError([
                                          "travelSettlement",
                                          0,
                                          field.name,
                                        ]);

                                        return (
                                          <>
                                            <Form.Item
                                              name={[
                                                "travelSettlement",
                                                0,
                                                field.name,
                                              ]}
                                              rules={[
                                                {
                                                  required: true,
                                                  message: field.message,
                                                },
                                              ]}
                                              noStyle
                                            >
                                              <Input
                                                disabled
                                                placeholder={field.placeholder}
                                                className={`form-control rounded-md ${
                                                  errors.length
                                                    ? "ant-input-status-error"
                                                    : ""
                                                }`}
                                              />
                                            </Form.Item>

                                            {errors.length > 0 && (
                                              <div className="ant-form-item-explain-error">
                                                {errors[0]}
                                              </div>
                                            )}
                                          </>
                                        );
                                      }}
                                    </Form.Item>
                                  </div>
                                </Col>
                              ))}
                            </Row>
                          </div>
                        )}

                        {changedExpenseValues?.natureOfExpense ===
                          "Travel Settlement" &&
                          locationCurrency &&
                          locationCurrency !== "INR" &&
                          (() => {
                            const hasExistingAdvance =
                              Number(
                                changedExpenseValues?.travelSettlement?.[0]
                                  ?.advanceAmount ||
                                  changedExpenseValues?.advanceAmount ||
                                  0,
                              ) > 0;
                            return (
                              <div className="travelSettlement">
                                <h3>Advance Amount</h3>
                                <Checkbox
                                  checked={
                                    showAdvanceAmount || hasExistingAdvance
                                  }
                                  onChange={(e) => {
                                    const checked = e.target.checked;

                                    // When user checks → just enable
                                    if (checked) {
                                      setShowAdvanceAmount(true);
                                      return;
                                    }

                                    // When user UNCHECKS → confirm first
                                    Modal.confirm({
                                      title: "Remove Advance Amount?",
                                      content:
                                        "This will clear the advance amount and remove attached files. Do you want to continue?",
                                      okText: "Yes",
                                      cancelText: "No",
                                      className: "revokeOrDeleteConfirmModal",

                                      okButtonProps: {
                                        className: "defaultbutton",
                                      },
                                      cancelButtonProps: {
                                        className: "defaultWhiteBtn",
                                      },

                                      onOk: () => {
                                        resetAdvanceAndMedia(); // ✅ clear everything
                                      },

                                      onCancel: () => {
                                        // Revert checkbox
                                        setShowAdvanceAmount(true);
                                      },
                                    });
                                  }}
                                >
                                  Any Advance Amount?
                                </Checkbox>

                                {(showAdvanceAmount || hasExistingAdvance) && (
                                  <Row gutter={[18, 24]} className="mt-8">
                                    {/* ================= ADVANCE INPUT ================= */}
                                    <Col xs={24} md={24} xl={24}>
                                      <div className="form-row-wrap">
                                        <label>Advance Amount (INR)</label>

                                        <Form.Item shouldUpdate noStyle>
                                          {() => {
                                            const errors = form.getFieldError([
                                              "travelSettlement",
                                              0,
                                              "advanceAmount",
                                            ]);

                                            return (
                                              <>
                                                <Form.Item
                                                  name={[
                                                    "travelSettlement",
                                                    0,
                                                    "advanceAmount",
                                                  ]}
                                                  rules={[
                                                    {
                                                      validator: (_, value) => {
                                                        if (
                                                          value === undefined ||
                                                          value === ""
                                                        ) {
                                                          return Promise.resolve();
                                                        }
                                                        if (Number(value) < 0) {
                                                          return Promise.reject(
                                                            "Advance amount cannot be negative",
                                                          );
                                                        }
                                                        return Promise.resolve();
                                                      },
                                                    },
                                                  ]}
                                                  noStyle
                                                >
                                                  <Input
                                                    type="number"
                                                    min={0}
                                                    className={`form-control h-45px rounded-md ${
                                                      errors.length
                                                        ? "ant-input-status-error"
                                                        : ""
                                                    }`}
                                                    onChange={(e) => {
                                                      const advance = Number(
                                                        e.target.value || 0,
                                                      );

                                                      setChangedExpenseValues(
                                                        (prev) => ({
                                                          ...prev,
                                                          advanceAmount:
                                                            advance,
                                                        }),
                                                      );
                                                    }}
                                                  />
                                                </Form.Item>

                                                {errors.length > 0 && (
                                                  <div className="ant-form-item-explain-error">
                                                    {errors[0]}
                                                  </div>
                                                )}
                                              </>
                                            );
                                          }}
                                        </Form.Item>
                                      </div>
                                    </Col>
                                    {/* ================= TRAVEL MEDIA ================= */}
                                    {/* <pre>{JSON.stringify(savedExpenseDetail,null,2)}</pre>
                                    <br />=======changedExpenseValues
                                    <pre>{JSON.stringify(changedExpenseValues,null,2)}</pre> */}
                                    <Col xs={24} md={12} xl={12}>
                                      <Form.Item shouldUpdate>
                                        <div>
                                          {/* Bind travelSettleMediaInfo to form */}
                                          <Form.Item
                                            name="travelSettleMediaInfo"
                                            noStyle
                                          >
                                            <Input type="hidden" />
                                          </Form.Item>

                                          {/* SHOW ALREADY SAVED FILES */}
                                          {changedExpenseValues
                                            ?.travelSettlement?.[0]
                                            ?.travelSettleMediaInfo?.length >
                                            0 &&
                                            changedExpenseValues?.travelSettlement?.[0]?.travelSettleMediaInfo?.map(
                                              (file, i) => (
                                                <div
                                                  className="symbol-100 symbol-light h-65 float-left"
                                                  key={`travelSettleMedia-${i}`}
                                                >
                                                  {changedExpenseValues?.empID ===
                                                    storeEmpID &&
                                                    changedExpenseValues?.status ===
                                                      process.env.REACT_APP_DRAFT_STATUS.toUpperCase() && (
                                                      <i
                                                        className="ki ki-close upload_close"
                                                        onClick={() =>
                                                          handleTravelMediaRemove(
                                                            i,
                                                          )
                                                        }
                                                      ></i>
                                                    )}

                                                  {file?.savedFilepath &&
                                                  [
                                                    "jpeg",
                                                    "jpg",
                                                    "png",
                                                  ].includes(
                                                    file?.savedFileType,
                                                  ) ? (
                                                    <SlideshowLightbox>
                                                      <img
                                                        className="rounded"
                                                        src={`${process.env.REACT_APP_BACKEND}/${file?.savedFilepath}`}
                                                        alt="media"
                                                        width="100"
                                                        height="100"
                                                        style={{
                                                          cursor: "pointer",
                                                        }}
                                                        onClick={() =>
                                                          handleImageClick(
                                                            `${process.env.REACT_APP_BACKEND}/${file?.savedFilepath}`,
                                                          )
                                                        }
                                                      />
                                                    </SlideshowLightbox>
                                                  ) : (
                                                    <div className="d-flex align-items-center justify-content-center h-65px w-65px">
                                                      <a
                                                        href={`${process.env.REACT_APP_BACKEND}/${file?.savedFilepath}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                      >
                                                        <i className="flaticon-file-2 uploadIcons"></i>
                                                      </a>
                                                    </div>
                                                  )}
                                                </div>
                                              ),
                                            )}

                                          {/* UPLOAD BUTTON (same conditions preserved) */}
                                          {changedExpenseValues?.status ===
                                            process.env.REACT_APP_DRAFT_STATUS.toUpperCase() && (
                                            <div>
                                              {changedExpenseValues
                                                ?.travelSettlement?.[0]
                                                ?.travelSettleMediaInfo
                                                ?.length === 0 && (
                                                <div className="symbol-100 symbol-light  h-65">
                                                  <Dragger
                                                    className="uploadDrag"
                                                    multiple={false}
                                                    customRequest={({
                                                      file,
                                                      onSuccess,
                                                    }) =>
                                                      handleTravelMediaChange(
                                                        file,
                                                        onSuccess,
                                                      )
                                                    }
                                                  >
                                                    <p className="ant-upload-drag-icon">
                                                      <img
                                                        src={media.upload.svg}
                                                      />
                                                    </p>
                                                    <p className="ant-upload-text">
                                                      Drag your file(s) to start
                                                      uploading
                                                    </p>
                                                    <p className="ant-upload-hint">
                                                      OR
                                                    </p>
                                                    <span className="uploadBtn">
                                                      <img
                                                        src={
                                                          media.uploadIcon.svg
                                                        }
                                                        alt="Upload info"
                                                        title="Allowed: jpg, png, pdf, docx (max 4MB)"
                                                      />{" "}
                                                      Browse file
                                                    </span>
                                                    <p>
                                                      Only one file allowed
                                                      (.jpg, .png, .pdf, max
                                                      4MB)
                                                    </p>
                                                  </Dragger>

                                                  {changedExpenseValues
                                                    ?.travelSettlement?.[0]
                                                    ?.travelSettleMediaInfo
                                                    ?.length > 0 && (
                                                    <Typography.Text>
                                                      {
                                                        changedExpenseValues
                                                          ?.travelSettlement?.[0]
                                                          ?.travelSettleMediaInfo
                                                          .length
                                                      }{" "}
                                                      file(s) attached
                                                    </Typography.Text>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                )}
                              </div>
                            );
                          })()}
                      </>
                    )}
                    {/* Travel Expense Section close here */}
                    <div className="main-repeater expenseAddRepeater">
                      <div
                        ref={stickyRef}
                        className={`addExpenseSubGroup sticky-bar ${
                          isSticky ? "is-sticky" : ""
                        }`}
                      >
                        <h3>Add Expense Details</h3>
                        <AddNewButton
                          addNewButtonClassName="addMoreBtn"
                          addNewButtonName="Add More"
                          btnType="button"
                          onClick={handleAddRow}
                          setShowLoader={setShowLoader}
                        />
                      </div>
                      <Table
                        className="table"
                        dataSource={rowsWithIds}
                        rowKey="id"
                        pagination={false}
                        showHeader={false}
                      >
                        <Column
                          title="Expense Details"
                          key="expenseDetails"
                          render={(text, record, index) => (
                            <div className="flex flex-col gap-2">
                              <h3 className="text-dark font-size-lg expenseRepeaterDetailHeading">
                                {record.title || `Expense Detail #${index + 1}`}
                              </h3>

                              <Row gutter={[18, 12]}>
                                {/* ---------------------- DATE ---------------------- */}
                                <Col xs={24} md={12} xl={12}>
                                  <Form.Item
                                    shouldUpdate
                                    //name={["rows", index, "date"]}
                                    rules={validationRules.date}
                                  >
                                    <div className="form-row-wrap">
                                      <label
                                        htmlFor="date"
                                        className="block mb-1 font-medium"
                                      >
                                        Date
                                        {validationRules.date.some(
                                          (r) => r.required,
                                        ) && (
                                          <span className="mandatory-symbol">
                                            *
                                          </span>
                                        )}
                                      </label>

                                      <Form.Item
                                        name={["rows", index, "date"]}
                                        rules={validationRules.date}
                                        noStyle
                                      >
                                        <DatePicker
                                          disabledDate={(current) => {
                                            const minDate = new Date(
                                              blockedDate?.blockedOn,
                                            );
                                            return (
                                              current &&
                                              current.toDate() <=
                                                getMinDate(minDate)
                                            );
                                          }}
                                          format="DD-MM-YYYY"
                                          placeholder="Select Date"
                                          className="form-control h-45px rounded-md d-inline-flex"
                                          onChange={(date) => {
                                            const formatted = date
                                              ? date.format("YYYY-MM-DD")
                                              : null;
                                            handleChange(
                                              index,
                                              "date",
                                              formatted,
                                            );
                                          }}
                                        />
                                      </Form.Item>
                                    </div>
                                  </Form.Item>
                                </Col>

                                {/* ---------------------- DESCRIPTION ---------------------- */}
                                <Col xs={24} md={12} xl={12}>
                                  <Form.Item
                                    shouldUpdate
                                    //name={["rows", index, "description"]}
                                    rules={validationRules.description}
                                  >
                                    <div className="form-row-wrap">
                                      <label className="block mb-1 font-medium">
                                        Description
                                        {validationRules.description.some(
                                          (r) => r.required,
                                        ) && (
                                          <span className="mandatory-symbol">
                                            *
                                          </span>
                                        )}
                                      </label>
                                      <Form.Item
                                        name={["rows", index, "description"]}
                                        rules={validationRules.description}
                                        noStyle
                                      >
                                        <Input
                                          className="form-control h-45px rounded-md"
                                          onChange={(e) =>
                                            handleChange(
                                              index,
                                              "description",
                                              e.target.value,
                                            )
                                          }
                                        />
                                      </Form.Item>
                                    </div>
                                  </Form.Item>
                                </Col>

                                {/* ---------------------- DEPARTMENT ---------------------- */}
                                <Col xs={24} md={12} xl={12}>
                                  <Form.Item
                                    shouldUpdate
                                    //name={["rows", index, "departmentID"]}
                                    rules={validationRules.departmentID}
                                  >
                                    <div className="form-row-wrap">
                                      <label className="block mb-1 font-medium">
                                        Department
                                        {validationRules.departmentID.some(
                                          (r) => r.required,
                                        ) && (
                                          <span className="mandatory-symbol">
                                            *
                                          </span>
                                        )}
                                      </label>

                                      <Form.Item
                                        name={["rows", index, "departmentID"]}
                                        rules={validationRules.departmentID}
                                        noStyle
                                      >
                                        <Select
                                          showSearch
                                          onChange={(value) =>
                                            handleChange(
                                              index,
                                              "departmentID",
                                              value,
                                            )
                                          }
                                          className="h-45px rounded-md"
                                          options={departments}
                                          filterOption={(input, option) =>
                                            option?.label
                                              ?.toLowerCase()
                                              ?.includes(input.toLowerCase())
                                          }
                                        />
                                      </Form.Item>
                                    </div>
                                  </Form.Item>
                                </Col>

                                {/* ---------------------- EXPENSE CODE ---------------------- */}
                                <Col xs={24} md={12} xl={12}>
                                  <Form.Item
                                    shouldUpdate
                                    //name={["rows", index, "expenseCodeID"]}
                                    rules={validationRules.expenseCodeID}
                                  >
                                    <div className="form-row-wrap">
                                      <label className="block mb-1 font-medium">
                                        Expense Code
                                        {validationRules.expenseCodeID.some(
                                          (r) => r.required,
                                        ) && (
                                          <span className="mandatory-symbol">
                                            *
                                          </span>
                                        )}
                                      </label>

                                      <Form.Item
                                        name={["rows", index, "expenseCodeID"]}
                                        rules={validationRules.expenseCodeID}
                                        noStyle
                                      >
                                        <Select
                                          showSearch
                                          onChange={(value) =>
                                            handleChange(
                                              index,
                                              "expenseCodeID",
                                              value,
                                            )
                                          }
                                          className="h-45px rounded-md"
                                          options={expenseCodes}
                                          filterOption={(input, option) =>
                                            option?.label
                                              ?.toLowerCase()
                                              ?.includes(input.toLowerCase())
                                          }
                                        />
                                      </Form.Item>
                                    </div>
                                  </Form.Item>
                                </Col>

                                {/* ---------------------- CURRENCY ---------------------- */}
                                <Col xs={24} md={12} xl={12}>
                                  <Form.Item
                                    shouldUpdate
                                    //name={["rows", index, "currencyID"]}
                                    rules={validationRules.currencyID}
                                  >
                                    <div className="form-row-wrap">
                                      <label className="block mb-1 font-medium">
                                        Currency
                                        {validationRules.currencyID.some(
                                          (r) => r.required,
                                        ) && (
                                          <span className="mandatory-symbol">
                                            *
                                          </span>
                                        )}
                                      </label>

                                      <Form.Item
                                        name={["rows", index, "currencyID"]}
                                        rules={validationRules.currencyID}
                                        noStyle
                                      >
                                        <Select
                                          showSearch
                                          className="h-45px rounded-md"
                                          disabled={
                                            changedExpenseValues?.natureOfExpense ===
                                              "Travel Settlement" &&
                                            Boolean(locationCurrency)
                                          }
                                          options={
                                            changedExpenseValues?.natureOfExpense ===
                                            "Travel Settlement"
                                              ? allCurrencies.filter(
                                                  (c) =>
                                                    c.label ===
                                                    locationCurrency,
                                                )
                                              : allCurrencies
                                          }
                                          filterOption={(input, option) =>
                                            option?.label
                                              ?.toLowerCase()
                                              ?.includes(input.toLowerCase())
                                          }
                                          onChange={(value) =>
                                            handleChange(
                                              index,
                                              "currencyID",
                                              value,
                                            )
                                          }
                                        />
                                      </Form.Item>
                                    </div>
                                  </Form.Item>
                                </Col>

                                {/* ---------------------- RECHARGE ---------------------- */}
                                <Col xs={24} md={12} xl={12}>
                                  <Form.Item
                                    shouldUpdate
                                    //name={["rows", index, "recharge"]}
                                    rules={validationRules.recharge}
                                  >
                                    <div className="form-row-wrap">
                                      <label className="block mb-1 font-medium">
                                        Recharge
                                        {validationRules?.recharge.some(
                                          (r) => r.required,
                                        ) && (
                                          <span className="mandatory-symbol">
                                            *
                                          </span>
                                        )}
                                      </label>

                                      <Form.Item
                                        name={["rows", index, "recharge"]}
                                        rules={validationRules.recharge}
                                        noStyle
                                      >
                                        <Select
                                          showSearch
                                          onChange={(value) =>
                                            handleChange(
                                              index,
                                              "recharge",
                                              value,
                                            )
                                          }
                                          className="h-45px rounded-md"
                                          options={rechargeOpts}
                                          filterOption={(input, option) =>
                                            option?.label
                                              ?.toLowerCase()
                                              ?.includes(input.toLowerCase())
                                          }
                                        />
                                      </Form.Item>
                                    </div>
                                  </Form.Item>
                                </Col>

                                {/* ---------------------- COST ---------------------- */}
                                <Col xs={24} md={24} xl={24}>
                                  <Form.Item
                                    shouldUpdate
                                    //name={["rows", index, "cost"]}
                                    rules={validationRules.cost}
                                  >
                                    <div className="form-row-wrap">
                                      <label className="block mb-1 font-medium">
                                        Cost
                                        {validationRules.cost.some(
                                          (r) => r.required,
                                        ) && (
                                          <span className="mandatory-symbol">
                                            *
                                          </span>
                                        )}
                                      </label>

                                      <Form.Item
                                        name={["rows", index, "cost"]}
                                        rules={validationRules.cost}
                                        noStyle
                                      >
                                        <Input
                                          type="text"
                                          className="form-control h-45px rounded-md"
                                          onChange={(e) =>
                                            handleChange(
                                              index,
                                              "cost",
                                              e.target.value,
                                            )
                                          }
                                        />
                                      </Form.Item>
                                    </div>
                                  </Form.Item>
                                </Col>
                                {/* ---------------------- UPLOAD (ORIGINAL LOGIC KEPT SAME) ---------------------- */}
                                <Col xs={24} md={12} xl={12}>
                                  <Form.Item shouldUpdate>
                                    <div>
                                      {/* BIND mediaInfo to form WITHOUT changing UI */}
                                      <Form.Item
                                        name={["rows", index, "mediaInfo"]}
                                        noStyle
                                      >
                                        <Input type="hidden" />
                                      </Form.Item>
                                      {/* SHOW ALREADY SAVED FILES */}
                                      {record?.mediaInfo?.length > 0 &&
                                        mediaItem?.["item_" + index] ===
                                          undefined &&
                                        record?.mediaInfo?.map((file, i) => (
                                          <div
                                            className="symbol-100 symbol-light h-65 float-left"
                                            key={`media-${index}-${i}`}
                                          >
                                            {changedExpenseValues?.empID ===
                                              storeEmpID &&
                                              changedExpenseValues?.status ===
                                                process.env.REACT_APP_DRAFT_STATUS.toUpperCase() && (
                                                <i
                                                  className="ki ki-close upload_close"
                                                  onClick={() =>
                                                    handleMediaRemove(
                                                      record,
                                                      index,
                                                    )
                                                  }
                                                ></i>
                                              )}

                                            {file?.savedFilepath &&
                                            ["jpeg", "jpg", "png"].includes(
                                              file?.savedFileType,
                                            ) ? (
                                              <SlideshowLightbox>
                                                <img
                                                  className="rounded"
                                                  src={`${process.env.REACT_APP_BACKEND}/${file?.savedFilepath}`}
                                                  alt="media"
                                                  width="100"
                                                  height="100"
                                                  style={{ cursor: "pointer" }}
                                                  onClick={() =>
                                                    handleImageClick(
                                                      `${process.env.REACT_APP_BACKEND}/${file?.savedFilepath}`,
                                                    )
                                                  }
                                                />
                                              </SlideshowLightbox>
                                            ) : (
                                              <div className="d-flex align-items-center justify-content-center h-65px w-65px">
                                                <a
                                                  href={`${process.env.REACT_APP_BACKEND}/${file?.savedFilepath}`}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                >
                                                  <i className="flaticon-file-2 uploadIcons"></i>
                                                </a>
                                              </div>
                                            )}
                                          </div>
                                        ))}

                                      {/* UPLOAD BUTTON (same conditions preserved) */}
                                      {changedExpenseValues?.status ===
                                        process.env.REACT_APP_DRAFT_STATUS.toUpperCase() && (
                                        <div>
                                          {(mediaItem?.["item_" + index] ===
                                            "removed" ||
                                            mediaItem?.["item_" + index] ===
                                              "freshUpload" ||
                                            record?.mediaInfo?.length ===
                                              0) && (
                                            <div className="symbol-100 symbol-light  h-65">
                                              <input
                                                id={`fileInput${index}`}
                                                type="file"
                                                accept=".png, .jpg, .jpeg, .pdf, .docx, .doc"
                                                onChange={(event) =>
                                                  handleFileChange(index, event)
                                                }
                                                style={{ display: "none" }}
                                              />

                                              <Dragger
                                                className="uploadDrag"
                                                multiple={false}
                                                customRequest={({
                                                  file,
                                                  onSuccess,
                                                }) =>
                                                  handleFileChange(
                                                    index,
                                                    file,
                                                    onSuccess,
                                                  )
                                                }
                                              >
                                                <p className="ant-upload-drag-icon">
                                                  <img src={media.upload.svg} />
                                                </p>
                                                <p className="ant-upload-text">
                                                  Drag your file(s) to start
                                                  uploading
                                                </p>
                                                <p className="ant-upload-hint">
                                                  OR
                                                </p>
                                                <span className="uploadBtn">
                                                  <img
                                                    src={media.uploadIcon.svg}
                                                    alt="Upload info"
                                                    title="Allowed: jpg, png, pdf, docx (max 4MB)"
                                                  />{" "}
                                                  Browse file
                                                </span>
                                                <p>
                                                  Only one file allowed (.jpg,
                                                  .png, .pdf, max 4MB)
                                                </p>
                                              </Dragger>

                                              {record?.mediaInfo?.length >
                                                0 && (
                                                <Typography.Text>
                                                  {record.mediaInfo.length}{" "}
                                                  file(s) attached
                                                </Typography.Text>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </Form.Item>
                                </Col>

                                {/* ---------------------- REMOVE ROW ---------------------- */}
                                {rows?.length > 1 && (
                                  <i
                                    className="flaticon-delete icon-lg cursor-pointer"
                                    onClick={() => handleRemoveRow(index)}
                                  ></i>
                                )}
                              </Row>
                            </div>
                          )}
                        />
                      </Table>
                    </div>
                    <div style={{ padding: "0 9px" }}>
                      <Row gutter={[18, 24]}>
                        <Col xs={24}>
                          <div className="my-5"></div>
                        </Col>
                      </Row>
                    </div>
                    <div className="text-center footerBtn">
                      <div className="footerReversment">
                        <small>Total Reimbursement</small>
                        <h4>
                          {process.env.REACT_APP_DEFAULT_CURRENCY}
                          {totalReimbursement}
                        </h4>
                      </div>
                      <div className="btnsWrap">
                        <SubmitButton
                          htmlType="submit"
                          submitName="Save"
                          className="defalutWhiteBtn"
                          onClick={() => {
                            setFormAction("draft");
                          }}
                        />
                        {id && (
                          <button
                            type="button"
                            className="btn rounded-lg defalutWhiteBtn mr-10"
                            onClick={() => {
                              handleDelete({
                                title: "Discard",
                                selectedRowKeys: [id],
                                dispatch,
                                deleteThunk: discardExpenseThunk,
                                route: "list-of-expense",
                                actionName: "expenseRequest",
                                onSuccess: () => {
                                  //  Stop loader
                                  setShowLoader(false);

                                  // Close modal
                                  setIsVisible(false);

                                  //  Tell parent to refresh
                                  if (onCloseReset) onCloseReset();
                                },
                              });
                            }}
                          >
                            <span>Discard</span>
                          </button>
                        )}
                        <SubmitButton
                          htmlType="submit"
                          submitName="Publish"
                          className="defaultbutton"
                          onClick={() => {
                            setFormAction("");
                          }}
                        />
                      </div>
                    </div>
                    <Modal
                      title={
                        formAction
                          ? `${formAction.charAt(0).toUpperCase()}${formAction.slice(1)} Expense`
                          : "Confirm Expense"
                      }
                      open={isModalVisible}
                      onCancel={handleCancel}
                      className="custom-modal"
                      footer={[
                        <Button
                          key="back"
                          className="btn px-5 py-2 defaultWhiteBtn rounded-lg"
                          onClick={handleCancel}
                        >
                          No
                        </Button>,
                        <Button
                          key="submit"
                          className="btn px-5 py-2 defaultbutton rounded-lg"
                          onClick={handleOk}
                        >
                          Yes
                        </Button>,
                      ]}
                    >
                      {formAction === "draft" ? (
                        <p>
                          Please review your entered information before saving
                          the form as a draft. You can make changes later before
                          final submission from <strong>List of Expense</strong>
                          .
                        </p>
                      ) : (
                        <p>
                          Please review your selected or entered information
                          carefully before submitting the form, as changes may
                          not be possible once it's submitted.
                        </p>
                      )}
                    </Modal>
                  </Form>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
